import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../contexts/kato.context';
import { useShared } from '../../../contexts/shared.context';
import KatoSelects from '../components/kato-selects.component';
import FooterComponent from '../components/footer.component';
import Field from '../../../components/field/field.component';
import { toast } from 'react-toastify';
import { calculateRoadSharePercentage, getObjectTotals, getRegionTotals, isEmpty, t } from '../../../helpers/utils';
import { GeneralInfoStatus, IRoad, IRoadShareStats } from '../../../types/kato.types';
import { updateGeneralInfoData } from '../../../requests/supervisor.request';
import classes from './styles.module.scss';
import { ChevronRightIcon, PlusIcon } from '../../../assets/icons';

const baseClass = 'road-tab'
const translationKey = 'admin.road'

const roadRegionKeys = [
  'prevYearGoodConditionKm', 'prevYearGoodConditionPercentage', 'prevYearSatisfactoryConditionKm', 'prevYearSatisfactoryConditionPercentage',
  'prevYearPoorConditionKm', 'prevYearPoorConditionPercentage', 'curYearGoodConditionKm', 'curYearGoodConditionPercentage', 'curYearSatisfactoryConditionKm',
  'curYearSatisfactoryConditionPercentage', 'curYearPoorConditionKm', 'curYearPoorConditionPercentage'
]

const createNewRoadGroup = (): IRoadShareStats => ({
  id: Date.now(),
  name: ``,
  totalKm: 0,
  curYearTotalFineRoadPercentage: 0,
  prevYearTotalFineRoadPercentage: 0,
  totalCurYearGoodConditionKm: 0,
  totalCurYearGoodConditionPercentage: 0,
  totalCurYearPoorConditionKm: 0,
  totalCurYearPoorConditionPercentage: 0,
  totalCurYearSatisfactoryConditionKm: 0,
  totalCurYearSatisfactoryConditionPercentage: 0,
  totalPrevYearGoodConditionKm: 0,
  totalPrevYearGoodConditionPercentage: 0,
  totalPrevYearPoorConditionKm: 0,
  totalPrevYearPoorConditionPercentage: 0,
  totalPrevYearSatisfactoryConditionKm: 0,
  totalPrevYearSatisfactoryConditionPercentage: 0,
  roadsList: [],
})

const createNewRoad = (): IRoad => ({
  id: Date.now(),
  index: ``,
  roadName: '',
  totalKm: 0,
  prevYearGoodConditionKm: 0,
  prevYearGoodConditionPercentage: 0,
  prevYearSatisfactoryConditionKm: 0,
  prevYearSatisfactoryConditionPercentage: 0,
  prevYearPoorConditionKm: 0,
  prevYearPoorConditionPercentage: 0,
  curYearGoodConditionKm: 0,
  curYearGoodConditionPercentage: 0,
  curYearSatisfactoryConditionKm: 0,
  curYearSatisfactoryConditionPercentage: 0,
  curYearPoorConditionKm: 0,
  curYearPoorConditionPercentage: 0,
  ongoingProjects: '',
})


const RoadTab = () => {
  const { i18n: { language } } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo, currentLevel } = useKato();
  const { basicStats, status, totalRoadShareStats } = katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();

  const [formData, setFormData] = useState({
    basicStats: basicStats || [],
    status: status || GeneralInfoStatus.IN_PROCESS,
    totalRoadShareStats: totalRoadShareStats || {},
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [openObjects, setOpenObjects] = useState<{ [key: string]: boolean }>({});
  const [isAddingRoadGroup, setIsAddingRoadGroup] = useState(false);
  const [isAddingRoad, setIsAddingRoad] = useState(false);

  const [newRoadGroup, setNewRoadGroup] = useState<IRoadShareStats | null>(null);
  const [newRoad, setNewRoad] = useState<IRoad | null>(null);

  const roadFields = (lang: 'ru' | 'kz') => [
    {
      title: t(`${translationKey}.road-list.year-result`, lang, { year: selectedInfoYear - 1 }),
      conditions: [
        {
          name: t(`${translationKey}.road-list.good`, lang),
          fields: ['prevYearGoodConditionKm', 'prevYearGoodConditionPercentage',]
        },
        {
          name: t(`${translationKey}.road-list.satisfactory`, lang),
          fields: ['prevYearSatisfactoryConditionKm', 'prevYearSatisfactoryConditionPercentage',]
        },
        {
          name: t(`${translationKey}.road-list.not-satisfactory`, lang),
          fields: ['prevYearPoorConditionKm', 'prevYearPoorConditionPercentage',]
        },
      ]
    },
    {
      title: t(`${translationKey}.road-list.year-result`, lang, { year: selectedInfoYear }),
      conditions: [
        {
          name: t(`${translationKey}.road-list.good`, lang),
          fields: ['curYearGoodConditionKm', 'curYearGoodConditionPercentage',]
        },
        {
          name: t(`${translationKey}.road-list.satisfactory`, lang),
          fields: ['curYearSatisfactoryConditionKm', 'curYearSatisfactoryConditionPercentage',]
        },
        {
          name: t(`${translationKey}.road-list.not-satisfactory`, lang),
          fields: ['curYearPoorConditionKm', 'curYearPoorConditionPercentage',]
        },
      ]
    },
  ]

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        basicStats: formData.basicStats,
        status: formData.status,
        totalRoadShareStats: formData.totalRoadShareStats,
      }
      updateGeneralInfoData(
        katoGeneralInfo.kato,
        data,
        selectedInfoYear
      ).then(() => {
        updatedGeneralInfo();
        toast.success(t(`admin.success`, language));
      });
    }
  }

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    // const fields = formData.basicStats.filter(stat => stat.type === 'INFRASTRUCTURE');

    // const hasAtLeastOneValue = fields.some(stat =>
    //   stat.previousYearVal != null || stat.currentYearVal != null
    // );

    // if (hasAtLeastOneValue) {
    //   for (const stat of fields) {
    //     if (isEmpty(stat.previousYearVal) || isEmpty(stat.currentYearVal) || isEmpty(stat.additionalInfo?.previousYearVal) || isEmpty(stat.additionalInfo?.currentYearVal)) {
    //       newErrors[stat.subType] = true;
    //     }
    //   }
    // }
    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  useEffect(() => {
    setFormData({
      basicStats: basicStats || [],
      status: status || GeneralInfoStatus.IN_PROCESS,
      totalRoadShareStats: totalRoadShareStats || {},
    });
  }, [basicStats, katoGeneralInfo.kato, selectedInfoYear, totalRoadShareStats]);

  const handleStartAdd = () => {
    setNewRoadGroup(
      {
        ...createNewRoadGroup()
      }
    );
    setIsAddingRoadGroup(true);
  };

  const handleStartAddRoad = () => {
    setNewRoad(
      {
        ...createNewRoad()
      }
    );
    setIsAddingRoad(true);
  };

  const handleCancelAddRoad = () => {
    setNewRoad(null);
    setIsAddingRoad(false);
  };

  const handleAddRoadToGroup = () => {
    if (!newRoadGroup || !newRoad) return;

    setNewRoadGroup(prev => ({
      ...prev!,
      roadsList: [...(prev?.roadsList || []), newRoad],
    }));

    setNewRoad(null);
    setIsAddingRoad(false);
  };

  const handleCancelAddRoadGroup = () => {
    setNewRoadGroup(null);
    setNewRoad(null);
    setIsAddingRoadGroup(false);
    setIsAddingRoad(false);
  };

  const handleAddRoadGroup = () => {
    if (!newRoadGroup) return;

    setFormData(prev => ({
      ...prev,
      totalRoadShareStats: {
        ...prev.totalRoadShareStats,
        roadShareStats: [
          ...(prev.totalRoadShareStats.roadShareStats || []),
          newRoadGroup,
        ],
      },
    }));

    setNewRoadGroup(null);
    setNewRoad(null);
    setIsAddingRoadGroup(false);
    setIsAddingRoad(false);
  };


  const renderFields = (lang: 'ru' | 'kz') => {
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';
    return <div className={classes['fields-container']}>
      {/* {formData.basicStats.filter(item => item.type === 'ROAD').map((item) => (
        <div className={classes['field-block']} key={`${item.type}-${item.subType}-${lang}`}></div>
      ))} */}

      {
        formData.totalRoadShareStats && formData?.totalRoadShareStats?.roadShareStats?.length > 0 && <div className={classes['field-block']}>
          <div className={classes['field-block__title']}>{t(`${translationKey}.title`, lang)}</div>
          <div className={classes[`${baseClass}__grid`]}>
            <div className={classes[`${baseClass}__grid__header`]}>
              <span>{t(`${translationKey}.totalKm`, lang)}</span>
              <span>{getRegionTotals(formData?.totalRoadShareStats?.roadShareStats, 'totalKm')}</span>
            </div>
            <div className={classes[`${baseClass}__grid__body`]}>
              {roadRegionKeys.map((key) => <div className={classes[`${baseClass}__grid__item`]} key={key}>
                <span>{t(`${translationKey}.${key}`, lang, { year: key.includes('prev') ? selectedInfoYear - 1 : selectedInfoYear })}</span>
                <span>{getRegionTotals(formData?.totalRoadShareStats?.roadShareStats, key)}</span>
              </div>)}
            </div>
          </div>
          <div className={classes['field-block']}>
            {
              formData?.totalRoadShareStats?.roadShareStats?.map((stat, groupIdx) => {
                return (
                  <div className={classes['field-block']} key={`${stat.name}-${lang}`}>
                    <div
                      className={classes['field-block__title']}
                      onClick={() => setOpenObjects(prev => ({ ...prev, [stat.name]: !prev[stat.name] }))}
                    >
                      {stat.name}
                      <ChevronRightIcon
                        style={{
                          transform: openObjects[stat.name] ? 'rotate(0deg)' : 'rotate(90deg)',
                          transition: 'transform 0.3s',
                        }}
                      />
                    </div>

                    {openObjects[stat.name] && (
                      <>
                        <div className={classes[`${baseClass}__grid`]}>
                          <div className={classes[`${baseClass}__grid__header`]}>
                            <span>{t(`${translationKey}.totalKm`, lang)}</span>
                            <span>{getObjectTotals(stat.roadsList, 'totalKm')}</span>
                          </div>
                          <div className={classes[`${baseClass}__grid__body`]}>
                            {roadRegionKeys.map((key) => (
                              <div className={classes[`${baseClass}__grid__item`]} key={key}>
                                <span>{t(`${translationKey}.${key}`, lang, {
                                  year: key.includes('prev') ? selectedInfoYear - 1 : selectedInfoYear,
                                })}</span>
                                <span>{getObjectTotals(stat.roadsList, key)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {stat.roadsList.map((road, roadIdx) => (
                          <div className={classes['field-block']} key={`${road.index}-${lang}`}>
                            <div className={classes['field-block__title']}>{road.roadName}</div>

                            {['totalKm', 'index'].map((key) => {
                              const val = road[key as keyof typeof road] ?? '';
                              return (
                                <Field
                                  key={`${road.index}-${key}-${lang}`}
                                  label={t(`${translationKey}.road-list.${key}`, lang)}
                                  value={val}
                                  onChange={(newVal) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      totalRoadShareStats: {
                                        ...prev.totalRoadShareStats,
                                        roadShareStats: prev.totalRoadShareStats.roadShareStats.map((group, gIdx) =>
                                          gIdx === groupIdx
                                            ? {
                                              ...group,
                                              roadsList: group.roadsList.map((r, rIdx) =>
                                                rIdx === roadIdx
                                                  ? {
                                                    ...r,
                                                    [key]: newVal,
                                                  }
                                                  : r
                                              ),
                                            }
                                            : group
                                        ),
                                      },
                                    }));
                                  }}
                                />
                              );
                            })}

                            {roadFields(lang).map((field, i) => (
                              <div key={`field-group-${i}`} className={classes['field-block__fields']}>
                                <div className={classes['field-block__title']}>{field.title}</div>

                                <div className={classes['field-block__fields']}>
                                  {field.conditions.map((condition, j) => (
                                    <div key={`condition-${j}`}>
                                      <div className={classes['field-block__title']}>{condition.name}</div>
                                      <div className={classes['field-block__fields']}>
                                        {condition.fields.map((fieldKey) => {
                                          const value = fieldKey.includes('Percentage')
                                            ? calculateRoadSharePercentage(+road[fieldKey.replace('Percentage', 'Km') as keyof typeof road], road.totalKm)
                                            : road[fieldKey as keyof typeof road] ?? '';
                                          return (
                                            <Field
                                              key={`${road.index}-${fieldKey}-${lang}`}
                                              label={t(`${translationKey}.road-list.${fieldKey}`, lang)}
                                              value={value}
                                              onChange={(newVal) => {
                                                setFormData(prev => ({
                                                  ...prev,
                                                  totalRoadShareStats: {
                                                    ...prev.totalRoadShareStats,
                                                    roadShareStats: prev.totalRoadShareStats.roadShareStats.map((group, gIdx) =>
                                                      gIdx === groupIdx
                                                        ? {
                                                          ...group,
                                                          roadsList: group.roadsList.map((r, rIdx) =>
                                                            rIdx === roadIdx
                                                              ? {
                                                                ...r,
                                                                [fieldKey]: newVal,
                                                              }
                                                              : r
                                                          ),
                                                        }
                                                        : group
                                                    ),
                                                  },
                                                }));
                                              }}
                                              error={errors[`road-${road.index}-${fieldKey}`]}
                                              disabled={fieldKey.includes('Percentage')}
                                              type='number'
                                            />
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })
            }

            {
              !isAddingRoadGroup && (
                <div className={classes['field-block__actions']}>
                  <button onClick={handleStartAdd}><PlusIcon /> {t(`actions.add`, lang)}</button>
                </div>
              )
            }

            {
              isAddingRoadGroup && newRoadGroup && (
                <div className={classes['field-block']}>
                  {['name', 'totalKm'].map((key) => {
                    const fieldKey = key as keyof Omit<IRoadShareStats, 'roadsList'>;
                    return (
                      <Field
                        key={`${newRoadGroup.id}-${key}-${lang}`}
                        label={t(`${translationKey}.${key}`, lang)}
                        value={newRoadGroup[fieldKey] ?? ''}
                        onChange={(newVal) => {
                          const val = newVal === '' ? '' : newVal;
                          setNewRoadGroup(prev => ({
                            ...prev as IRoadShareStats,
                            [fieldKey]: val,
                          }))
                        }}
                        error={errors[`road-group-${newRoadGroup.id}-${key}`]}
                        type='text'
                      />
                    )
                  })}


                  {
                    newRoadGroup.roadsList.map((road, roadIdx) => (
                      <div className={classes['field-block']} key={`${road.index}-${lang}`}>
                        {
                          ['roadName', 'index', 'totalKm'].map((key) => {
                            const val = road[key as keyof typeof road] === '' ? '' : road[key as keyof typeof road];
                            return <Field
                              key={`${road.index}-${key}-${lang}`}
                              label={t(`${translationKey}.road-list.${key}`, lang)}
                              value={val ?? ''}
                              onChange={(newVal) => {
                                const val = newVal === '' ? '' : +newVal;
                                setNewRoad(prev => ({
                                  ...prev as IRoad,
                                  [key]: val,
                                }));
                              }}
                              error={errors[`road-${road.index}-${key}`]}
                              type={key === 'totalKm' ? 'number' : 'text'}
                            />
                          })
                        }
                        {roadFields(lang).map((field, i) => (
                          <div key={`field-group-${i}`} className={classes['field-block__fields']}>
                            <div className={classes['field-block__title']}>{field.title}</div>

                            <div className={classes['field-block__fields']}>
                              {field.conditions.map((condition, j) => (
                                <div key={`condition-${j}`}>
                                  <div className={classes['field-block__title']}>{condition.name}</div>
                                  <div className={classes['field-block__fields']}>
                                    {condition.fields.map((fieldKey) => {
                                      const value = fieldKey.includes('Percentage')
                                        ? calculateRoadSharePercentage(+road[fieldKey.replace('Percentage', 'Km') as keyof typeof road], road.totalKm)
                                        : road[fieldKey as keyof typeof road] ?? '';
                                      return (
                                        <Field
                                          key={`${road.index}-${fieldKey}-${lang}`}
                                          label={t(`${translationKey}.road-list.${fieldKey}`, lang)}
                                          value={value}
                                          onChange={(newVal) => {
                                            const val = newVal === '' ? '' : +newVal;
                                            setNewRoad(prev => ({
                                              ...prev as IRoad,
                                              [fieldKey]: val,
                                            }));
                                          }}
                                          error={errors[`road-${road.index}-${fieldKey}`]}
                                          disabled={fieldKey.includes('Percentage')}
                                          type='number'
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))
                  }

                  {/* Добавление дороги внутрь новой группы */}
                  {!isAddingRoad && (
                    <div className={classes['field-block__actions']}>
                      <button onClick={handleStartAddRoad}><PlusIcon /> {t(`actions.add`, lang)}</button>
                    </div>
                  )}

                  {isAddingRoad && newRoad && (
                    <div className={classes['field-block']}>
                      {
                        ['roadName', 'index', 'totalKm'].map((key) => {
                          const val = newRoad[key as keyof typeof newRoad] === '' ? '' : newRoad[key as keyof typeof newRoad];
                          return <Field
                            key={`${newRoad.index}-${key}-${lang}`}
                            label={t(`${translationKey}.road-list.${key}`, lang)}
                            value={val ?? ''}
                            onChange={(newVal) => {
                              const val = newVal === '' ? '' : newVal;
                              setNewRoad(prev => ({
                                ...prev as IRoad,
                                [key]: val,
                              }));
                            }}
                            error={errors[`road-${newRoad.index}-${key}`]}
                            type={key === 'totalKm' ? 'number' : 'text'}
                          />
                        })
                      }
                      {roadFields(lang).map((field, i) => (
                        <div key={`field-group-${i}`} className={classes['field-block__fields']}>
                          <div className={classes['field-block__title']}>{field.title}</div>

                          <div className={classes['field-block__fields']}>
                            {field.conditions.map((condition, j) => (
                              <div key={`condition-${j}`}>
                                <div className={classes['field-block__title']}>{condition.name}</div>
                                <div className={classes['field-block__fields']}>
                                  {condition.fields.map((fieldKey) => {
                                    const value = fieldKey.includes('Percentage')
                                      ? calculateRoadSharePercentage(+newRoad[fieldKey.replace('Percentage', 'Km') as keyof typeof newRoad], newRoad.totalKm)
                                      : newRoad[fieldKey as keyof typeof newRoad] ?? '';
                                    return (
                                      <Field
                                        key={`${newRoad.index}-${fieldKey}-${lang}`}
                                        label={t(`${translationKey}.road-list.${fieldKey}`, lang)}
                                        value={value}
                                        onChange={(newVal) => {
                                          const val = newVal === '' ? '' : +newVal;
                                          setNewRoad(prev => ({
                                            ...prev as IRoad,
                                            [fieldKey]: val,
                                          }));
                                        }}
                                        error={errors[`road-${newRoad.index}-${fieldKey}`]}
                                        disabled={fieldKey.includes('Percentage')}
                                        type='number'
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      <div className={classes['field-block__actions']}>
                        <button onClick={handleAddRoadToGroup}>
                          <PlusIcon /> {t('actions.save', lang)}
                        </button>
                        <button onClick={handleCancelAddRoad}>{t('actions.cancel', lang)}</button>
                      </div>
                    </div>
                  )}

                  {/* Сохранить группу дорог, если добавление дороги завершено */}
                  {!isAddingRoad && (
                    <div className={classes['field-block__actions']}>
                      <button onClick={handleAddRoadGroup}>
                        <PlusIcon /> {t('actions.save', lang)}
                      </button>
                      <button onClick={handleCancelAddRoadGroup}>{t('actions.cancel', lang)}</button>
                    </div>
                  )}
                </div>
              )
            }

          </div>

        </div>
      }
    </div >
  }

  if (!basicStats || !katoGeneralInfo) return null;

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        <KatoSelects lang={language as 'ru' | 'kz'} />
        <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  )
}

export default RoadTab